import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useFetchListData, useFetch } from '@/composables'
import type { SecurityDatum } from '@/store/security/types'

const useSecurityStore = defineStore('security', () => {
  const error = ref<string | null>(null)
  const securityMap = ref<Map<string, boolean>>(new Map())

  const {
    data: riskParamsData,
    loading: riskParamsLoading,
    fetchData: riskParamsFetchData,
  } = useFetch({
    url: '/api/security/get_risk_params',
    autoFetch: false,
  })

  const {
    data: securityData,
    loading: securityLoading,
    fetchData: securityFetchData,
  } = useFetchListData<SecurityDatum[]>({
    url: '/api/security/list',
    autoFetch: false,
  })

  // 根据模块代码获取风控配置
  const getSecurityByModelCode = computed(() => {
    return (modelCode: string) => {
      return securityData.value?.find((item) => item.model_code === modelCode)
    }
  })

  // 根据模块值获取风控配置
  const getSecurityByModelValue = computed(() => {
    return (modelValue: string) => {
      return securityData.value?.find((item) => item.model_value === modelValue)
    }
  })

  // 检查模块是否启用
  const isModuleEnabled = computed(() => {
    return (modelCode: string) => {
      const security = securityData.value?.find((item) => item.model_code === modelCode)
      return security?.status === '1'
    }
  })

  // 获取启用的风控模块
  const enabledModules = computed(() => {
    return securityData.value?.filter((item) => item.status === '1')
  })

  const ensure = async (): Promise<void> => {
    const list = await securityFetchData()
    securityMap.value.clear()
    if (!list) return
    list.forEach((i) => {
      if (!i.model_value) return
      securityMap.value.set(i.model_value, true)
    })
  }

  const isRequired = async (securityCode: string): Promise<boolean> => {
    await ensure()
    return securityMap.value.has(securityCode)
  }

  // 重置状态
  const reset = () => {
    securityLoading.value = false
    riskParamsLoading.value = false
    securityData.value = []
    riskParamsData.value = []
    error.value = null
  }

  // 幂等初始化器

  return {
    // 状态
    securityLoading,
    riskParamsLoading,
    securityData,
    riskParamsData,
    error,

    // 方法
    securityFetchData,
    riskParamsFetchData,
    isRequired,
    ensure,
    reset,

    // 计算属性
    getSecurityByModelCode,
    getSecurityByModelValue,
    isModuleEnabled,
    enabledModules,
  }
})

export default useSecurityStore
